/** One complete SSE frame. Event names default to message; heartbeat-only blocks are omitted. */
interface SseFrame {
  event: string;
  id?: string;
  data: string;
}

/** Parse accumulated SSE text, retaining the incomplete tail for the next decoded chunk.
 * CRLF normalization also handles a CR/LF pair split between reads. Data lines retain all but one
 * leading space after the colon and join with newlines, including empty lines. Desktop supplies its
 * existing reassembly ceiling; the CLI leaves the stream unbounded. Payload validation stays with
 * each consumer. onComment reports complete blocks containing a colon-prefixed comment line, once
 * per block, without creating a data frame. This importless source is mirrored byte-for-byte into web/lib/sse.ts. */
export function parseSse(
  buffer: string,
  maxBufferLength?: number,
  onComment?: () => void,
): { frames: SseFrame[]; rest: string } {
  buffer = buffer.replaceAll('\r\n', '\n');
  if (maxBufferLength !== undefined && buffer.length > maxBufferLength) {
    throw new Error('SSE event exceeded its reassembly bound.');
  }
  const frames: SseFrame[] = [];
  let end: number;
  while ((end = buffer.indexOf('\n\n')) >= 0) {
    const block = buffer.slice(0, end);
    buffer = buffer.slice(end + 2);
    let event = 'message';
    let id: string | undefined;
    const lines: string[] = [];
    let hasComment = false;
    for (const line of block.split('\n')) {
      if (line.startsWith(':')) hasComment = true;
      else if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('id:')) id = line.slice(3).trim();
      else if (line.startsWith('data:')) {
        const data = line.slice(5);
        lines.push(data.startsWith(' ') ? data.slice(1) : data);
      }
    }
    if (hasComment) onComment?.();
    if (lines.length) frames.push({ event, ...(id ? { id } : {}), data: lines.join('\n') });
  }
  return { frames, rest: buffer };
}
